<script lang="ts">
  import {
    deleteUserData,
    updateName,
    deleteAnswer,
    updateSpecificAnswer,
    deleteSet,
    pseudoAnswer,
    isPseudoAnswer
  } from "../services/cleanup";
  import { isAdmin, isSuperAdmin } from "../stores/store";
  import { readable_question } from "../utils/readable_question";

  export let firstRowInBlock;
  export let finalRowInBlock;
  export let rowsInBlock;
  export let item;
  export let user;
  export let updated = null;
  export let projectId;
  
  let error = false;
  let removeError = false;
  let replacement = "";
  let updateEnabled = true;
  let isUpdating = false;
  let isDeleting = false;
  let isDeletingSet = false;
  let isGlobalUpdating = false;
  let isPseudonymizing = false;
  let pseudoState = null;

  /**
   * Check if this answer can be globally updated (has an ID indicating pseudonymized data)
   */
  $: canGlobalUpdate = item.answer && 
                      typeof item.answer === 'object' && 
                      item.answer.id && 
                      item.answer.id.trim() !== '' &&
                      item.answer.data !== undefined;

  //$:  console.log('canGlobalUpdate', canGlobalUpdate, item.answer, typeof item.answer, item.answer.id, item.answer.data !== undefined)
  

  /**
   * Get display value - handles both plain text and pseudonymized data
   */
  $: displayValue = (() => {
    if (canGlobalUpdate) {
      return item.answer.data || ''; // Pseudonymized data - show the decrypted value
    } else {
      return item.answer || ''; // Plain data
    }
  })();

  /*$: if (item && !canGlobalUpdate) {
    checkPseudoState();
  }

  const checkPseudoState = async () => {
    try {
      const setIndex = extractNumericId(item.setKey);
      const questionIndex = extractNumericId(item.identifier);

      if (setIndex && questionIndex) {
        const result = await isPseudoAnswer(
          user,
          projectId,
          setIndex,
          questionIndex,
          item.outer,
          item.inner
        );
        pseudoState = result === true;
      }
    } catch (error) {
      console.error('Error checking pseudo state:', error);
      pseudoState = null;
    }
  };*/

  const extractNumericId = (identifier) => {
    if (!identifier) return null;
    const match = identifier.toString().match(/\d+$/);
    return match ? match[0] : null;
  };

  /**
   * Global update - ONLY for pseudonymized data (with ID)
   * This updates ALL instances of this name across the entire project
   */
  const globalUpdate = async () => {
    if (isGlobalUpdating || !canGlobalUpdate) return;
    
    error = false;
    if (!replacement || replacement.trim() === "") {
      error = true;
      return;
    }

    isGlobalUpdating = true;
    try {
      console.log("=== GLOBAL UPDATE ===");
      console.log('Global update - updating name with ID:', item.answer.id, 'to:', replacement.trim());
      
      const result = await updateName(projectId, item.answer.id, replacement.trim());
      console.log('Global update result:', result);
      
      if (result && result.success) {
        console.log("✅ Global update successful");
        // Update local display
        item.answer.data = replacement.trim();
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Global update failed:', result);
        error = true;
      }
    } catch (err) {
      console.error('❌ Error during global update:', err);
      error = true;
    } finally {
      isGlobalUpdating = false;
    }
  };

  /**
   * Single update - works for ANY answer (plain or pseudonymized)
   * This updates ONLY this specific answer instance
   */
  const update = async () => {
    if (isUpdating) return;
    
    error = false;
    if (replacement.trim().length === 0) {
      error = true;
      return;
    }

    isUpdating = true;
    try {
      // Extract the numeric identifiers
      const setIndex = item.setKey.match(/\d+$/)?.[0];
      const questionIndex = item.identifier.match(/\d+$/)?.[0];

      if (!setIndex || !questionIndex) {
        throw new Error('Could not extract set or question index');
      }

      console.log("=== SINGLE UPDATE ===");
      console.log('Single update - updating specific answer:', {
        user,
        setIndex,
        questionIndex,
        outer: item.outer,
        inner: item.inner,
        newValue: replacement.trim(),
        currentValue: displayValue
      });

      const result = await updateSpecificAnswer(
        projectId,
        user,
        setIndex,
        questionIndex,
        item.outer,
        item.inner,
        replacement.trim()
      );

      if (result && result.success) {
        console.log("✅ Single update successful");
        // Update local data - handle both types
        if (canGlobalUpdate) {
          item.answer.data = replacement.trim();
        } else {
          item.answer = replacement.trim();
        }
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Single update failed:', result);
        error = true;
      }
    } catch (err) {
      console.error('❌ Error during single update:', err);
      error = true;
    } finally {
      isUpdating = false;
    }
  };

  /**
   * Pseudonymize answer
   */
  /*const pseudonymize = async () => {
    if (isPseudonymizing || canGlobalUpdate) return; // Can't pseudo already pseudonymized data
    
    isPseudonymizing = true;
    removeError = false;
    
    try {
      const setIndex = extractNumericId(item.setKey);
      const questionIndex = extractNumericId(item.identifier);

      if (!setIndex || !questionIndex) {
        throw new Error('Could not extract set or question index');
      }

      console.log("=== PSEUDONYMIZE ANSWER ===");
      console.log('Pseudonymizing answer:', {
        user,
        projectId,
        setIndex,
        questionIndex,
        item: item.outer,
        innerIndex: item.inner,
        currentAnswer: displayValue
      });

      const result = await pseudoAnswer(
        user,
        projectId,
        setIndex,
        questionIndex,
        item.outer,
        item.inner
      );

      if (result && result.success) {
        console.log('✅ Pseudonymize successful');
        pseudoState = true;
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Pseudonymize failed:', result);
        removeError = true;
      }
    } catch (err) {
      console.error('❌ Error during pseudonymize:', err);
      removeError = true;
    } finally {
      isPseudonymizing = false;
    }
  };*/

  /**
   * Delete all user data
   */
  const globalDel = async () => {
    if (isDeleting || !confirm('Are you sure you want to delete ALL data for this user? This cannot be undone.')) return;
    
    isDeleting = true;
    try {
      console.log("=== GLOBAL DELETE USER ===");
      console.log("Deleting all data for user:", user);
      
      const result = await deleteUserData(projectId, user);
      if (result && result.success) {
        console.log("✅ Global delete successful");
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Global delete failed:', result);
      }
    } catch (err) {
      console.error('❌ Error during global delete:', err);
    } finally {
      isDeleting = false;
    }
  };

  /**
   * Remove specific answer - works for ANY answer type
   */
  const del = async () => {
    if (isDeleting || !confirm('Are you sure you want to delete this answer?')) return;
    
    isDeleting = true;
    removeError = false;
    
    try {
      // Extract numeric identifiers
      const setIndex = extractNumericId(item.setKey);
      const questionIndex = extractNumericId(item.identifier);

      if (!setIndex || !questionIndex) {
        throw new Error('Could not extract set or question index');
      }

      console.log("=== DELETE ANSWER ===");
      console.log('Deleting answer:', {
        user,
        projectId,
        setIndex,
        questionIndex,
        item: item.outer,
        innerIndex: item.inner,
        currentAnswer: displayValue,
        answerType: canGlobalUpdate ? 'pseudonymized' : 'plain'
      });

      const result = await deleteAnswer(
        user, 
        projectId, 
        setIndex, 
        questionIndex, 
        item.outer, 
        item.inner
      );

      if (result && result.success) {
        console.log('✅ Delete successful');
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Delete failed:', result);
        removeError = true;
      }
    } catch (err) {
      console.error('❌ Error during delete:', err);
      removeError = true;
    } finally {
      isDeleting = false;
    }
  };

  /**
   * Delete entire set of answers for this user
   */
  const delSet = async () => {
    if (isDeletingSet || !confirm('Are you sure you want to delete this entire set? This cannot be undone.')) return;
    
    isDeletingSet = true;
    try {
      // Extract set index
      const setIndex = extractNumericId(item.setKey);
      
      if (!setIndex) {
        throw new Error('Could not extract set index');
      }

      console.log("=== DELETE SET ===");
      console.log("Deleting set:", setIndex, "for user:", user);

      const result = await deleteSet(user, projectId, setIndex);

      if (result && result.success) {
        console.log("✅ Delete set successful");
        clearEditState();
        if (updated) {
          updated();
        }
      } else {
        console.error('❌ Delete set failed:', result);
      }
    } catch (err) {
      console.error('❌ Error during delete set:', err);
    } finally {
      isDeletingSet = false;
    }
  };

  /**
   * Clear edit state
   */
  const clearEditState = () => {
    updateEnabled = true;
    replacement = "";
    removeError = false;
    error = false;
  };

  /**
   * Toggle edit mode
   */
  const toggleEdit = () => {
    updateEnabled = !updateEnabled;
    removeError = false;
    error = false;
    replacement = updateEnabled ? "" : displayValue; // Pre-populate with current value when editing
  };

  // Check if any operation is in progress
  $: isProcessing = isUpdating || isDeleting || isDeletingSet || isGlobalUpdating || isPseudonymizing;
</script>

<tr class:is-tight={!finalRowInBlock}>
  {#if $isAdmin}
    <td class="remove-padding">
      <button 
        class="btn" 
        class:btnhighlight={!updateEnabled} 
        disabled={isProcessing}
        on:click={toggleEdit}
      >
        {isProcessing ? 'Processing...' : (updateEnabled ? 'Edit' : 'Cancel')}
      </button>
    </td>

    <td class="is-narrow" class:has-error={removeError}>
      {readable_question(item.identifier)}
    </td>
    
    <td>
      {displayValue}
      <!--{#if canGlobalUpdate}
        <span class="data-type-indicator">(pseudonymized)</span>
      {:else if pseudoState === true}
        <span class="data-type-indicator">(pseudo)</span>
      {:else if pseudoState === false}
        <span class="data-type-indicator">(plain)</span>
      {:else}
        <span class="data-type-indicator">(checking...)</span>
      {/if}-->
    </td>
    
    <td>
      <input 
        class:has-error={error} 
        bind:value={replacement} 
        type="text" 
        disabled={updateEnabled || isProcessing}
        placeholder={displayValue}
      />
    </td>

    {#if item.type === 'setSelf'}
      <td class="remove-padding" colspan="2">
        <button 
          class="btn full-btn" 
          class:hidden={updateEnabled} 
          disabled={isProcessing}
          on:click={update}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </td>
    {:else}
      <td class="remove-padding no-edge">
        <button 
          class="btn" 
          class:hidden={updateEnabled} 
          disabled={isProcessing}
          on:click={update}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </td>
      <td class="remove-padding">
        <button 
          class="btn" 
          class:hidden={updateEnabled} 
          disabled={isProcessing}
          on:click={del}
        >
          {isDeleting ? 'Removing...' : 'Remove'}
        </button>
      </td>
    {/if}
    
    <td class="is-danger">
      {#if canGlobalUpdate}
        <button 
          class="btn" 
         
          disabled={isProcessing}
          on:click={globalUpdate}
        >
          {isGlobalUpdating ? 'Updating All...' : 'Global Update'}
        </button>
      {:else}
        <span class="disabled-text">
          {pseudoState === true ? 'Already Pseudo' : 'No Action Available'}
        </span>
      {/if}
    </td>
    
    {#if firstRowInBlock}
      <td class="is-danger">
        <button 
          class="btn" 
          disabled={isProcessing}
          on:click={delSet}
        >
          {isDeletingSet ? 'Deleting...' : 'Delete Set'}
        </button>
      </td>
      <td class="is-danger">
        <button 
          class="btn" 
          disabled={isProcessing}
          on:click={globalDel}
        >
          {isDeleting ? 'Deleting...' : 'Delete User'}
        </button>
      </td>
    {:else}
      <td class="is-danger"/>
      <td class="is-danger"/>
    {/if}

  {:else}
    <td class="is-narrow">
      {readable_question(item.identifier)} 
    </td>
    <td>
      {displayValue}
      <!--{#if canGlobalUpdate}
        <span class="data-type-indicator">(pseudonymized)</span>
      {:else if pseudoState === true}
        <span class="data-type-indicator">(pseudo)</span>
      {:else if pseudoState === false}
        <span class="data-type-indicator">(plain)</span>
      {/if}-->
    </td>
  {/if}
</tr>

<!-- Error messages -->
{#if error}
  <tr>
    <td colspan="8" class="error-message">
      Error: Please enter a valid value
    </td>
  </tr>
{/if}

{#if removeError}
  <tr>
    <td colspan="8" class="error-message">
      Error: Could not remove this item. It may have dependencies.
    </td>
  </tr>
{/if}

<style lang="scss">
  button {
    line-height: 18px;
    font-weight: normal;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .is-danger button {
    width: 140px;
  }
  
  td.is-danger {
    width: 148px;
  }

  tr.is-tight td.is-danger {
    border-color: #fc72cd;
    border-bottom: none;
  }

  td.is-danger:not(:last-child) {
    border-right-width: 0;
    padding-right: 0;
    width: 144px;
  }
  
  td.is-danger:last-child {
    text-align: right;
  }

  th.is-danger {
    width: 160px;
  }
  
  input.has-error {
    border: 1px solid red;
  }
  
  td.has-error {
    color: red;
  }
  
  .hidden {
    visibility: hidden;
  }

  .full-btn {
    width: 100%;
  }

  .btnhighlight {
    background-color: #fc72cd;
  }

  .disabled-text {
    color: #999;
    font-style: italic;
    font-size: 0.9em;
  }

  .data-type-indicator {
    color: #666;
    font-size: 0.8em;
    font-style: italic;
    margin-left: 8px;
  }

  .error-message {
    color: red;
    font-style: italic;
    text-align: center;
    padding: 8px;
    background-color: #ffe6e6;
  }

  input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
</style>